import { useEffect, useCallback, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { api } from '../utils/api';
import { useEditorStore } from '../store/editorStore';
import { useConfirmStore } from '../store/confirmStore';
import { useUpdateDraft, useCreateDraft, usePublishDraft } from './useDataCenterApi';
import { buildStagedChangesJson, mapApiRack } from '../utils/draftUtils';
import { PPM } from '../store/editorStore';
import { shouldAutosaveCreateDraft } from '../utils/editorDrafts.logic';

export function useDraftAutoSave(
  code: string,
  currentDraftId: number | null,
  setCurrentDraftId: (id: number | null) => void,
  dc: any,
  hadDraftId: MutableRefObject<boolean>,
) {
  const { setSaveStatus, saveStatus } = useEditorStore();
  const updateDraftMut = useUpdateDraft(code);
  const createDraftMut = useCreateDraft(code);
  const publishDraftMut = usePublishDraft(code);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const executeSave = useCallback(async (draftId: number) => {
    const store = useEditorStore.getState();
    try {
      const racksJson = buildStagedChangesJson(store.racks, store.baselineRacks, store.rows);
      const defaultTrayZ = (store.dcSettings?.defaultRackHeightM ?? 2.0) + (store.dcSettings?.defaultTrayHeightM ?? 0.3);
      const cableTraysJson = {
        nodes: Object.values(store.trayNodes).map((n: any) => {
          const zVal = n.z_m ?? n.z ?? defaultTrayZ;
          return {
            ...n,
            x: Number((n.x / PPM).toFixed(3)),
            y: Number((n.y / PPM).toFixed(3)),
            z: zVal,
            z_m: zVal,
          };
        }),
        edges: Object.values(store.trayEdges),
      };
      setSaveStatus('saving');
      // Serialize aisle_zones with metre coordinates for persistence
      const aisleZonesPayload = Object.fromEntries(
        Object.entries(store.aisleZones).map(([id, z]: [string, any]) => [
          id,
          { ...z, x_m: +(z.x / PPM).toFixed(3), y_m: +(z.y / PPM).toFixed(3), w_m: +(z.w / PPM).toFixed(3), h_m: +(z.h / PPM).toFixed(3) },
        ])
      );
      // Serialize racks_meta (status and role) for draft persistence
      const racksMetaPayload = Object.fromEntries(
        Object.values(store.racks).map((r: any) => [
          r.id,
          { status: r.status || 'active', role: r.role || 'Server' }
        ])
      );
      await updateDraftMut.mutateAsync({
        draftId,
        racks_json: racksJson,
        cable_trays_json: {
          ...cableTraysJson,
          rows_meta: store.rows,
          dc_settings: store.dcSettings,
          aisle_zones: aisleZonesPayload,
          racks_meta: racksMetaPayload,
        },
      });
      setSaveStatus('saved');
      const newRacks = { ...store.racks };
      Object.keys(newRacks).forEach(id => {
        if (newRacks[id].isDirty) newRacks[id] = { ...newRacks[id], isDirty: false };
      });
      useEditorStore.setState({ racks: newRacks, baselineRacks: JSON.parse(JSON.stringify(newRacks)) });
    } catch (err: any) {
      console.error('[AutoSave] executeSave error:', err);
      if (err?.response?.status === 403) {
        setSaveStatus('idle');
      } else {
        setSaveStatus('error');
        const baseline = store.baselineRacks;
        if (baseline && Object.keys(baseline).length > 0) {
          useEditorStore.setState({ racks: JSON.parse(JSON.stringify(baseline)) });
        }
      }
    }
  }, [updateDraftMut, setSaveStatus]);

  const handleSave = useCallback(async (source?: 'autosave') => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    let draftId = currentDraftId;
    let createdThisSave = false;
    if (!draftId) {
      if (source === 'autosave' && !shouldAutosaveCreateDraft(currentDraftId, hadDraftId.current)) {
        return;
      }
      try {
        const draft = await createDraftMut.mutateAsync({ name: `Черновик ${new Date().toLocaleString('ru-RU')}`, rows_count: dc?.rows_count ?? 1, max_racks_per_row: dc?.max_racks_per_row ?? 10 });
        draftId = draft.id;
        createdThisSave = true;
        // Defer setCurrentDraftId until after executeSave — otherwise useDraftLoader
        // fetches the empty new draft and wipes Wizard racks (audit P0).
      } catch (err: any) {
        console.error('[AutoSave] handleSave createDraft error:', err);
        const store = useEditorStore.getState();
        if (err?.response?.status === 403) {
          setSaveStatus('idle');
        } else {
          setSaveStatus('error');
          const baseline = store.baselineRacks;
          if (baseline && Object.keys(baseline).length > 0) {
            useEditorStore.setState({ racks: JSON.parse(JSON.stringify(baseline)) });
          }
        }
        return;
      }
    }
    await executeSave(draftId!);
    if (createdThisSave) setCurrentDraftId(draftId!);
  }, [currentDraftId, createDraftMut, dc, executeSave, setSaveStatus, setCurrentDraftId, hadDraftId]);

  useEffect(() => {
    if (saveStatus !== 'dirty') return;
    if (currentDraftId == null && !shouldAutosaveCreateDraft(currentDraftId, hadDraftId.current)) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => handleSave('autosave'), 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [saveStatus, handleSave, currentDraftId, hadDraftId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') { e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  const handlePublish = useCallback(async () => {
    if (!currentDraftId) return;
    await handleSave();
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Публикация схемы ЦОД',
      message: 'Опубликовать схему? Это обновит физическую карту ЦОД.',
      confirmText: 'Опубликовать',
      cancelText: 'Отмена'
    });
    if (!confirmed) return;
    try {
      await publishDraftMut.mutateAsync(currentDraftId);
      const r = await api.get(`/api/datacenters/${code}/racks`);
      const apiRacks: any[] = r.data || [];
      const racksRecord: Record<string, any> = {};
      apiRacks.forEach((rack: any) => {
        const mapped = mapApiRack(rack);
        racksRecord[mapped.id] = mapped;
      });
      useEditorStore.getState().loadRacks(racksRecord);
      useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));
      setSaveStatus('idle');
      setCurrentDraftId(null);
    } catch (err) {
      console.error('Ошибка публикации', err);
    }
  }, [currentDraftId, handleSave, publishDraftMut, code, setSaveStatus, setCurrentDraftId]);

  return { handleSave, handlePublish };
}
